import { useState, useCallback } from 'react';
import axios from 'axios';
import { EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import {
  NewsPost,
  NewsFilters,
  NewsFormData
} from '../types/EducationHub';

// Custom hook for news management
export const useNews = () => {
  const [news, setNews] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getAdminHeaders = () => {
    const token = sessionStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchNews = useCallback(async (filters: NewsFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.NEWS, {
        params: filters,
        headers: getAuthHeaders()
      });

      // Backend returns newsPosts, not news
      const data = response.data;
      setNews(data.newsPosts || []);
      setTotalCount(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (err: any) {
      // Always ensure news is an array, never undefined
      setNews([]);
      setTotalCount(0);
      setHasMore(false);
      
      // Only log errors that are not authentication related
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch news');
        console.error('Error fetching news:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getFeaturedNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.NEWS_FEATURED, {
        headers: getAuthHeaders()
      });

      const featuredNews: NewsPost[] = response.data;
      return featuredNews;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch featured news');
      console.error('Error fetching featured news:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Super-admin operations ──────────────────────────────────────────────────

  const fetchAdminNews = useCallback(async (): Promise<NewsPost[]> => {
    const { data } = await axios.get(EDUCATION_ENDPOINTS.NEWS_ADMIN, { headers: getAdminHeaders() });
    return data.newsPosts || [];
  }, []);

  const createNewsPost = useCallback(async (newsData: NewsFormData): Promise<NewsPost | null> => {
    setError(null);
    try {
      const { data } = await axios.post(EDUCATION_ENDPOINTS.NEWS, newsData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create news post');
      throw err;
    }
  }, []);

  const updateNewsPost = useCallback(async (id: string, newsData: Partial<NewsFormData>): Promise<NewsPost | null> => {
    setError(null);
    try {
      const { data } = await axios.put(EDUCATION_ENDPOINTS.NEWS_ITEM(id), newsData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update news post');
      throw err;
    }
  }, []);

  const deleteNewsPost = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await axios.delete(EDUCATION_ENDPOINTS.NEWS_ITEM(id), { headers: getAdminHeaders() });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete news post');
      throw err;
    }
  }, []);

  const getNewsById = useCallback(async (id: string): Promise<NewsPost | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${EDUCATION_ENDPOINTS.NEWS}/${id}`, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch news post');
      console.error('Error fetching news post:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    news,
    loading,
    error,
    totalCount,
    hasMore,
    fetchNews,
    getFeaturedNews,
    fetchAdminNews,
    createNewsPost,
    updateNewsPost,
    deleteNewsPost,
    getNewsById
  };
};

// Custom hook for news metadata
export const useNewsMetadata = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.NEWS_CATEGORIES, {
        headers: getAuthHeaders()
      });
      
      // Backend returns array directly, not wrapped in object
      setCategories(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch news categories');
      console.error('Error fetching news categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories
  };
};
